'use client';

import { ArrowRight, KeyRound, LoaderCircle, LogIn, Mail } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FormEvent, useState } from 'react';

type ApiErrorBody = Readonly<{ message?: string }>;

export function LoginForm({ activationCompleted = false }: Readonly<{ activationCompleted?: boolean }>) {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError('');
    const form = new FormData(event.currentTarget);

    try {
      const response = await fetch('/api/auth/sign-in/email', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          email: String(form.get('email') ?? ''),
          password: String(form.get('password') ?? ''),
          rememberMe: true,
        }),
      });
      if (!response.ok) {
        throw new Error(await responseMessage(response, '邮箱或密码不正确。'));
      }
      router.replace('/');
      router.refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit}>
      {activationCompleted ? <p className="form-message" role="status">账号已激活，请登录工作区。</p> : null}
      <label>
        <span>邮箱</span>
        <input name="email" type="email" autoComplete="email" required />
      </label>
      <label>
        <span>密码</span>
        <input name="password" type="password" autoComplete="current-password" minLength={8} maxLength={128} required />
      </label>
      <FormError message={error} />
      <button className="primary-button auth-submit" type="submit" disabled={pending}>
        {pending ? <LoaderCircle aria-hidden className="spin" size={18} /> : <LogIn aria-hidden size={18} />}
        {pending ? '正在登录' : '登录工作区'}
      </button>
      <div className="auth-links">
        <Link href="/reset-password">忘记密码</Link>
        <Link href="/activate">使用邀请链接激活</Link>
      </div>
    </form>
  );
}

export function ActivationForm({ token }: Readonly<{ token: string }>) {
  const router = useRouter();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(token ? '' : '邀请链接缺少一次性令牌，请重新打开邮件中的完整链接。');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setPending(true);
    setError('');
    const form = new FormData(event.currentTarget);

    try {
      const response = await fetch('/api/workspace/activation', {
        method: 'POST',
        headers: { ...jsonHeaders(), 'idempotency-key': crypto.randomUUID() },
        body: JSON.stringify({
          token,
          email: String(form.get('email') ?? ''),
          password: String(form.get('password') ?? ''),
          name: String(form.get('name') ?? ''),
        }),
      });
      if (!response.ok) {
        throw new Error(await responseMessage(response, '邀请无效、已使用或已过期。'));
      }
      const result = await readJsonBody(response);
      router.replace(result?.signInRequired === true ? '/login?activated=1' : '/');
      router.refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit}>
      <label>
        <span>姓名</span>
        <input name="name" type="text" autoComplete="name" maxLength={120} placeholder="用于团队成员识别" />
      </label>
      <label>
        <span>受邀邮箱</span>
        <input name="email" type="email" autoComplete="email" required />
      </label>
      <label>
        <span>设置密码</span>
        <input name="password" type="password" autoComplete="new-password" minLength={8} maxLength={128} required />
      </label>
      <FormError message={error} />
      <button className="primary-button auth-submit" type="submit" disabled={pending || !token}>
        {pending ? <LoaderCircle aria-hidden className="spin" size={18} /> : <ArrowRight aria-hidden size={18} />}
        {pending ? '正在激活' : '激活并进入工作区'}
      </button>
      <div className="auth-links auth-links-single">
        <Link href="/login">已有账号，返回登录</Link>
      </div>
    </form>
  );
}

export function ResetPasswordForm({ token }: Readonly<{ token: string }>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError('');
    setMessage('');
    const form = new FormData(event.currentTarget);

    try {
      const response = token
        ? await submitNewPassword(token, form)
        : await requestResetEmail(String(form.get('email') ?? ''));
      if (!response.ok) {
        throw new Error(await responseMessage(response, token ? '重置链接无效或已过期。' : '邮件服务暂不可用，请稍后重试。'));
      }
      setMessage(token ? '密码已更新，旧登录会话已撤销。' : '如果该邮箱存在，系统会发送密码重置邮件。');
      event.currentTarget.reset();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="auth-form" onSubmit={submit}>
      {token ? (
        <>
          <label>
            <span>新密码</span>
            <input name="password" type="password" autoComplete="new-password" minLength={8} maxLength={128} required />
          </label>
          <label>
            <span>确认新密码</span>
            <input name="confirmPassword" type="password" autoComplete="new-password" minLength={8} maxLength={128} required />
          </label>
        </>
      ) : (
        <label>
          <span>账号邮箱</span>
          <input name="email" type="email" autoComplete="email" required />
        </label>
      )}
      <FormError message={error} />
      {message ? <p className="form-message" role="status">{message}</p> : null}
      <button className="primary-button auth-submit" type="submit" disabled={pending}>
        {pending ? <LoaderCircle aria-hidden className="spin" size={18} /> : token ? <KeyRound aria-hidden size={18} /> : <Mail aria-hidden size={18} />}
        {pending ? '正在处理' : token ? '更新密码' : '发送重置邮件'}
      </button>
      <div className="auth-links auth-links-single">
        <Link href="/login">返回登录</Link>
      </div>
    </form>
  );
}

async function requestResetEmail(email: string): Promise<Response> {
  return fetch('/api/auth/request-password-reset', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email, redirectTo: '/reset-password' }),
  });
}

async function submitNewPassword(token: string, form: FormData): Promise<Response> {
  const password = String(form.get('password') ?? '');
  if (password !== String(form.get('confirmPassword') ?? '')) {
    throw new Error('两次输入的密码不一致。');
  }
  return fetch('/api/auth/reset-password', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ newPassword: password, token }),
  });
}

function jsonHeaders(): Record<string, string> {
  return { 'content-type': 'application/json', 'x-request-id': crypto.randomUUID() };
}

async function responseMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as ApiErrorBody;
    return body.message || fallback;
  } catch {
    return fallback;
  }
}

async function readJsonBody(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const body = (await response.json()) as unknown;
    return body && typeof body === 'object' && !Array.isArray(body) ? body as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '请求失败，请稍后重试。';
}

function FormError({ message }: Readonly<{ message: string }>) {
  return message ? <p className="form-error" role="alert">{message}</p> : null;
}
